import { useCallback, useEffect, useState } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import {
  convertLayoutWall,
  drawLayoutWall,
  fetchWallReview,
  removeLayoutWall,
  resetLayoutWalls,
} from "./planWallsApi";
import type {
  DetectedLayoutState,
  PlanWall,
  UsePlanWallsArgs,
  UsePlanWallsResult,
  WallKind,
  WallPoint,
  WallReview,
} from "./types";

const NO_WALLS: PlanWall[] = [];
Object.freeze(NO_WALLS);

const layoutStateOf = (review: WallReview): DetectedLayoutState =>
  review.summary ? { phase: "loaded", summary: review.summary } : { phase: "none" };

export const usePlanWalls = ({
  floorPlanId,
  pageIndex,
}: UsePlanWallsArgs): UsePlanWallsResult => {
  const [walls, setWalls] = useState<PlanWall[]>(NO_WALLS);
  const [areWallsShown, setAreWallsShown] = useState(true);
  const [didSaveFail, setDidSaveFail] = useState(false);
  const [detectedLayout, setDetectedLayout] = useState<DetectedLayoutState>({
    phase: "loading",
  });
  const [isCorrecting, setIsCorrectingState] = useState(false);
  const [selectedWallId, setSelectedWallId] = useState<string | null>(null);

  // Only what the server answers is shown: a wall on screen the router never received is one he would trust.
  const apply = useCallback(
    async (change: Promise<WallReview | null>): Promise<boolean> => {
      setDidSaveFail(false);
      const review = await change;
      if (!review) {
        setDidSaveFail(true);
        return false;
      }
      setWalls(review.walls);
      setDetectedLayout(layoutStateOf(review));
      return true;
    },
    [],
  );

  const addWall = useCallback(
    async (kind: WallKind, points: WallPoint[]) => {
      if (!floorPlanId || points.length < 2) return;
      const isSaved = await apply(
        drawLayoutWall({ floorPlanId, pageIndex }, kind, points),
      );
      if (!isSaved) return;
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_WALL_SAVED, {
        wallKind: kind,
        pointCount: points.length,
      });
    },
    [apply, floorPlanId, pageIndex],
  );

  const removeWall = useCallback(
    async (wallId: string) => {
      if (!floorPlanId) return;
      setSelectedWallId((selected) => (selected === wallId ? null : selected));
      const isSaved = await apply(
        removeLayoutWall({ floorPlanId, pageIndex }, wallId),
      );
      if (!isSaved) return;
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_WALL_REMOVED);
    },
    [apply, floorPlanId, pageIndex],
  );

  const toggleWallKind = useCallback(
    async (wallId: string) => {
      if (!floorPlanId) return;
      // The converted one is a new row with a new id, so the selection cannot follow it.
      setSelectedWallId(null);
      await apply(convertLayoutWall({ floorPlanId, pageIndex }, wallId));
    },
    [apply, floorPlanId, pageIndex],
  );

  const resetToDetected = useCallback(async () => {
    if (!floorPlanId) return;
    setSelectedWallId(null);
    await apply(resetLayoutWalls({ floorPlanId, pageIndex }));
  }, [apply, floorPlanId, pageIndex]);

  const setIsCorrecting = useCallback((next: boolean) => {
    setIsCorrectingState(next);
    if (!next) setSelectedWallId(null);
  }, []);

  useEffect(() => {
    setSelectedWallId(null);
    setIsCorrectingState(false);
    if (!floorPlanId) {
      setWalls(NO_WALLS);
      setDetectedLayout({ phase: "none" });
      return;
    }
    let isCurrent = true;
    setDetectedLayout({ phase: "loading" });
    const load = async () => {
      const review = await fetchWallReview({ floorPlanId, pageIndex });
      // The page may have changed while this was in flight.
      if (!isCurrent) return;
      // An unreadable page reads as "nothing detected": a step that can never load would lock the setup.
      setWalls(review?.walls ?? NO_WALLS);
      setDetectedLayout(review ? layoutStateOf(review) : { phase: "none" });
    };
    void load();
    return () => {
      isCurrent = false;
    };
  }, [floorPlanId, pageIndex]);

  return {
    walls,
    areWallsShown,
    setAreWallsShown,
    didSaveFail,
    addWall,
    removeWall,
    toggleWallKind,
    detectedLayout,
    resetToDetected,
    isCorrecting,
    setIsCorrecting,
    selectedWallId,
    selectWall: setSelectedWallId,
  };
};
